'use client';

import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import type {ProductProps} from '@/molecules/Cards/Product/typings';
import type Props from './typings';

import useExponeaRecommendations from '@/hooks/use-exponea-recommendations/use-exponea-recommendations';
import useProductsBySlugs from '@/hooks/use-products-by-slugs/use-products-by-slugs';
import ProductFeed from '@/organisms/ProductFeed/ProductFeed';
import {StyledColorBlock} from '@/pages/styles';
import {selectUserSettingsData} from '@/redux/reducers/userSettingsReducer';
import {pushGAPdpRecommendationEvent} from '@/utils/gtm';

export const getProductGAPayload = (products: ProductProps[], position: number) => {
  const {name: productName, artist_name: artistName, slug: productSlug} = products[position - 1];
  return {
    position,
    productName,
    artistName,
    productSlug,
  };
};

const MoreLikeThisSection = ({
  recommendationId,
  background,
  productId,
}: Props): JSX.Element | null => {
  const settings = useSelector(selectUserSettingsData);
  const exponeaRecommendations = useExponeaRecommendations({
    recommendationId,
    size: 10,
    productId,
  });
  const {data: products, status} = useProductsBySlugs({
    slugs: exponeaRecommendations.data.map((item) => item.slug),
    displaySizeUnits: settings.display_units,
    size: 4,
  });

  /**
   * @description Tracks the display of recommendation items.
   * only those ones that are in stock to be rendered
   */
  useEffect(() => {
    if (status === 'success') {
      const itemIdsInStock = products.map((item) => item.id.toString());
      exponeaRecommendations.handleTrackItemsDisplay(itemIdsInStock);
    }
  }, [status]);

  if (!recommendationId || products.length === 0) {
    return null;
  }
  return (
    <StyledColorBlock $background={background} aria-label="More like this products section">
      <ProductFeed
        products={products}
        title="More like this"
        gaEvents={{
          productClick: (position: number) => {
            pushGAPdpRecommendationEvent({
              ...getProductGAPayload(products, position),
              type: 'click',
            });
          },
          productFavourite: (position) => {
            pushGAPdpRecommendationEvent({
              ...getProductGAPayload(products, position),
              type: 'favourite',
            });
          },
        }}
      />
    </StyledColorBlock>
  );
};

export default MoreLikeThisSection;
